import { LockOutlined, MailOutlined } from '@ant-design/icons';
import { Button, Card, Flex, Form, Input, message, Spin, Typography } from 'antd';
import React, { useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import styled from 'styled-components';

import { useAuth } from '../context/AuthContext';
import { ROUTES } from '../routes/routes';

const { Title } = Typography;

interface LoginFormValues {
  email: string;
  password: string;
}

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100vh;
`;

const LoginWrapper = styled(Flex)`
  min-height: 100vh;
  background: #f4f7f6;
  justify-content: center;
  align-items: center;
`;

const LoginCard = styled(Card)`
  width: 460px;
  border-radius: 16px !important;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08) !important;
  border: 1px solid #edf2f7 !important;

  .ant-card-body {
    padding: 36px 40px !important;
  }
`;

const LogoMark = styled.div`
  text-align: center;
  margin-bottom: 8px;
  font-size: 24px;
  font-weight: 800;
  color: #ff8e8e;
  letter-spacing: 1px;
`;

const ForgotLink = styled(Link)`
  display: block;
  text-align: center;
  margin-top: 12px;
  font-size: 13px;
  color: rgba(0, 0, 0, 0.45);

  &:hover {
    color: #ff8e8e;
  }
`;

export const LoginPage: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const { login, isAuthenticated, isLoading } = useAuth();

  const handleSubmit = async (values: LoginFormValues) => {
    setLoading(true);
    try {
      await login(values.email, values.password);
    } catch (error: any) {
      message.error(error.message || 'Помилка входу');
    } finally {
      setLoading(false);
    }
  };

  if (isLoading) {
    return (
      <LoadingContainer>
        <Spin size="large" />
      </LoadingContainer>
    );
  }

  if (isAuthenticated) {
    return <Navigate to={ROUTES.DASHBOARD} />;
  }

  return (
    <LoginWrapper>
      <LoginCard>
        <LogoMark>AVON MARKET</LogoMark>
        <Title level={4} style={{ textAlign: 'center', margin: '0 0 24px' }}>
          Вхід адміністратора
        </Title>
        <Form layout="vertical" onFinish={handleSubmit} autoComplete="off">
          <Form.Item
            name="email"
            rules={[
              { required: true, message: 'Введіть email' },
              { type: 'email', message: 'Невірний формат email' },
            ]}
          >
            <Input prefix={<MailOutlined />} placeholder="Email" size="large" />
          </Form.Item>
          <Form.Item name="password" rules={[{ required: true, message: 'Введіть пароль' }]}>
            <Input.Password prefix={<LockOutlined />} placeholder="Пароль" size="large" />
          </Form.Item>
          <Form.Item>
            <Button type="primary" htmlType="submit" loading={loading} block size="large">
              Увійти
            </Button>
          </Form.Item>
        </Form>
        <ForgotLink to={ROUTES.FORGOT_PASSWORD}>Забули пароль?</ForgotLink>
      </LoginCard>
    </LoginWrapper>
  );
};
